/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import loadable from "@loadable/component";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { usePlayerCallbacks } from "../shared/context/PlayerCallbacksContext";
import { usePlayerConfig } from "../shared/context/PlayerConfigContext";
import {
  AssetPlaybackStatus,
  MediaStreamType,
  MediaType,
  PlayerControllerType,
  PlayerError,
  PlayerTimestamp,
  StandalonePlayerProps,
} from "../shared/types";
import { getFrameImageUrl } from "../shared/utils/image";

import {
  AssetInfoOverlay,
  ClosePlayerButton,
  ContinueWatchingOverlay,
} from "./Interface";

import "./StandaloneVideoPlayer.scss";

const MycujooPlayer = loadable(
  () => import("./Players/MycujooPlayer/MycujooPlayer"),
);

const THEOplayer = loadable(() => import("./Players/THEOplayer/THEOplayer"));

const VideoJsPlayer = loadable(
  () => import("./Players/VideoJsPlayer/VideoJsPlayer"),
  {
    fallback: <div>Loading...</div>,
  },
);

const JWPlayer = loadable(() => import("./Players/JWPlayer/JWPlayer"));

const Flowplayer = loadable(() => import("./Players/Flowplayer/Flowplayer"));

let intervalId: NodeJS.Timeout | undefined;

interface IPlayerActions {
  play: () => void;
  pause: () => void;
  seekTo: (time: number) => void;
  getCurrentTime: () => number | undefined;
}

const StandaloneVideoPlayer = (props: StandalonePlayerProps) => {
  const {
    autoplay = false,
    media,
    streamType,
    mediaPlayInfo,
    continueWatchingTimestamp,
    onGetUserId,
    onAnalyticsEvent,
    playerToken,
  } = props;
  const { playerType, progressSyncInterval } = usePlayerConfig();
  const { onProgressUpdate } = usePlayerCallbacks();
  const [isAssetInfoVisible, setIsAssetInfoVisible] = useState(
    !autoplay && playerType !== PlayerControllerType.Flowplayer,
  );
  const [isContinueWatchingDialogVisible, setIsContinueWatchingDialogVisible] =
    useState(false);
  const [isAdPlaying, setIsAdPlaying] = useState<boolean>(false);
  const lastTimeRef = useRef<number>(0);

  const anyValueGreaterThanZero = useCallback(
    (time: PlayerTimestamp): boolean => {
      return Object.values(time).some((value) => (value || 0) > 0);
    },
    [],
  );
  useEffect(() => {
    if (
      continueWatchingTimestamp &&
      anyValueGreaterThanZero(continueWatchingTimestamp)
    ) {
      setIsContinueWatchingDialogVisible(true);
    }
  }, [anyValueGreaterThanZero, continueWatchingTimestamp]);

  const player = useRef<IPlayerActions>();

  const showContinueWatching = useMemo(() => {
    return (
      continueWatchingTimestamp &&
      anyValueGreaterThanZero(continueWatchingTimestamp) &&
      mediaPlayInfo.Data?.MediaTypeCode !== MediaType.Live &&
      mediaPlayInfo.Data?.MediaTypeCode !== MediaType.Match &&
      mediaPlayInfo.Data?.MediaTypeCode !== MediaType.Channel &&
      mediaPlayInfo.Data?.MediaTypeCode !== MediaType.Program
    );
  }, [anyValueGreaterThanZero, continueWatchingTimestamp]);

  const sendTimeMarker = useCallback(
    (timeInSeconds: number, status?: AssetPlaybackStatus) => {
      console.log(`sendTimeMarker, time: ${timeInSeconds}, status: ${status}`);

      if (!media?.Media?.Id || streamType === MediaStreamType.Trial) {
        return;
      }

      lastTimeRef.current = timeInSeconds;

      onProgressUpdate?.({
        mediaId: media.Media.Id,
        progressInSeconds: timeInSeconds,
        status,
      }).catch((error: PlayerError) => {
        console.error("Unable to save watch progress", error);

        if (error?.MessageKey === "ASSET_CONCURRENT_PLAYBACK_LIMIT") {
          player.current?.pause();
        }
      });
    },
    [media?.Media?.Id, streamType, onProgressUpdate],
  );

  const saveWatchTime = useCallback(() => {
    const time = player.current?.getCurrentTime();

    if (media.Media && time != undefined) {
      sendTimeMarker(time, AssetPlaybackStatus.Starting);
    }
  }, [media.Media, sendTimeMarker]);

  const stopWatchProgressMonitoring = useCallback(() => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = undefined;
    }
  }, []);

  const startWatchProgressMonitoring = useCallback(() => {
    stopWatchProgressMonitoring();

    intervalId = setInterval(saveWatchTime, progressSyncInterval ?? 60000);
  }, [progressSyncInterval, saveWatchTime, stopWatchProgressMonitoring]);

  const onClickOverlay = () => {
    setIsAssetInfoVisible(false);
    player.current?.play();
  };

  const continueWatching = () => {
    if (continueWatchingTimestamp) {
      const { Hour, Minute, Second } = continueWatchingTimestamp;
      player.current?.seekTo(
        (Hour ?? 0) * 3600 + (Minute ?? 0) * 60 + (Second ?? 0),
      );
    }
    hideContinueWatchingDialog();
  };

  const hideContinueWatchingDialog = () => {
    player.current?.play();
    if (isContinueWatchingDialogVisible) {
      setIsContinueWatchingDialogVisible(false);
    }
  };

  const onPlay = useCallback(() => {
    setIsAssetInfoVisible(false);
    startWatchProgressMonitoring();

    const currentPlayerTime = Math.floor(player.current?.getCurrentTime() || 0);
    sendTimeMarker(currentPlayerTime, AssetPlaybackStatus.Streaming);
  }, [sendTimeMarker, startWatchProgressMonitoring]);

  const onPause = useCallback(() => {
    setIsAssetInfoVisible(true);
    stopWatchProgressMonitoring();

    const currentPlayerTime = Math.floor(player.current?.getCurrentTime() || 0);
    sendTimeMarker(currentPlayerTime, AssetPlaybackStatus.Paused);
  }, [sendTimeMarker, stopWatchProgressMonitoring]);

  const onEnded = useCallback(() => {
    const currentPlayerTime = Math.floor(player.current?.getCurrentTime() || 0);

    sendTimeMarker(currentPlayerTime, AssetPlaybackStatus.Ended);
  }, [player, sendTimeMarker]);

  const onAdStarted = () => {
    setIsAssetInfoVisible(false);
    setIsAdPlaying(true);
  };

  const onAdComplete = () => {
    setIsAdPlaying(false);
  };

  const onReady = useCallback(() => {
    !continueWatchingTimestamp && player.current?.play();
  }, [continueWatchingTimestamp]);

  useEffect(() => {
    return () => {
      stopWatchProgressMonitoring();
      const currentPlayerTime = Math.floor(
        player?.current?.getCurrentTime() || 0,
      );
      sendTimeMarker(
        currentPlayerTime === 0 && lastTimeRef.current > 0
          ? lastTimeRef.current
          : currentPlayerTime,
        AssetPlaybackStatus.Stopped,
      );
    };
  }, [sendTimeMarker, stopWatchProgressMonitoring]);

  const renderPlayer = () => {
    switch (playerType) {
      case PlayerControllerType.Mycujoo:
        return (
          <MycujooPlayer
            ref={player}
            eventId={media.Media?.ExternalId || ""}
            publicKey={playerToken || ""}
            showChromecast={false}
            showPictureInPicture={false}
            showContinueWatching={showContinueWatching}
            onVideoPause={onPause}
            onVideoPlay={onPlay}
            onGetUserId={onGetUserId}
          >
            <AssetInfoOverlay media={media} isVisible={isAssetInfoVisible} />
          </MycujooPlayer>
        );
      case PlayerControllerType.Flowplayer:
        return (
          <Flowplayer
            ref={player}
            media={media}
            mediaPlayInfo={mediaPlayInfo}
            autostart={!showContinueWatching}
            playerToken={playerToken}
            onPlay={onPlay}
            onPause={onPause}
            onEnded={onEnded}
            onReady={onReady}
            onAdStarted={onAdStarted}
            onAdComplete={onAdComplete}
          />
        );
      case PlayerControllerType.JWPlayer:
        return (
          <>
            <JWPlayer
              ref={player}
              media={media}
              mediaPlayInfo={mediaPlayInfo}
              autostart={!showContinueWatching}
              onPlay={onPlay}
              onPause={onPause}
              onReady={onReady}
              onAdStarted={onAdStarted}
              onAdComplete={onAdComplete}
            />
            <AssetInfoOverlay
              media={media}
              isVisible={isAssetInfoVisible && !isAdPlaying}
              onClick={onClickOverlay}
            />
          </>
        );
      case PlayerControllerType.THEOPlayer:
        return (
          <>
            <THEOplayer
              ref={player}
              media={media}
              mediaPlayInfo={mediaPlayInfo}
              onPlay={onPlay}
              onPause={onPause}
            />
            <AssetInfoOverlay
              media={media}
              isVisible={isAssetInfoVisible && !isAdPlaying}
              onClick={onClickOverlay}
            />
          </>
        );
      default:
        return (
          <>
            <VideoJsPlayer
              ref={player}
              media={media}
              mediaPlayInfo={mediaPlayInfo}
              onPlay={onPlay}
              onPause={onPause}
              onReady={onReady}
              onAnalyticsEvent={onAnalyticsEvent}
            />
            <AssetInfoOverlay
              media={media}
              isVisible={isAssetInfoVisible && !isAdPlaying}
              onClick={onClickOverlay}
            />
          </>
        );
    }
  };

  return (
    <div className="StandaloneVideoPlayer">
      <ClosePlayerButton />
      {renderPlayer()}
      {showContinueWatching && (
        <ContinueWatchingOverlay
          isVisible={isContinueWatchingDialogVisible}
          imageUrl={getFrameImageUrl(media.Media?.Images)}
          timeoutInSeconds={10}
          onPositiveClick={continueWatching}
          onNegativeClick={hideContinueWatchingDialog}
        />
      )}
    </div>
  );
};

export default StandaloneVideoPlayer;
